import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import AddressCell from '../components/AddressCell.js';
import ValueCell from '../components/ValueCell.js';
import WorkupCell from '../components/WorkupCell.js';
import EditableNotesComponent from '../components/EditableNotesComponent.js';
import CopyableIdCell from '../../../../common/CopyableIdCell.js';
import { convertAVToMV } from '../../../../utils/conversionUtils.js';
import OfferCell, { OfferEditCell } from '../components/GeneralizedOfferCell.js';
import { parseRowOfferValue } from '../services/offerGridUpdateService.js';
import { formatDateWithoutTimezone, formatCourtDateDisplayDay, parseCourtDateCalendarKey, isUnassignedCourtDateCase } from '../../../../common/dateUtils.js';
import { getGridStringOperators } from '@mui/x-data-grid-pro';
import logger from '../../../../utils/logger';
import { scarIndexSortComparator } from '../../../../utils/scarIndexSort.js';
import TownResponseCell, { TownIssueMarks } from '../../../TownView/TownResponseCell.js';
import PhotoCell from '../../Photos/PhotoCell.js';
import Box from '@mui/material/Box';
import {
  renderUnifiedTooltip,
  schoolDistrictIfmvTooltipLines,
} from '../utils/helpers.js';
import { CASE_REVIEW_COLUMNS } from '../../../../common/mappings';
import { getUserNameById } from '../../../UserContext.js';

export { hasRoundMismatch, isAventineOfferFromPriorRound, isTownOfferFromPriorRound } from '../../../../utils/mostRecentOfferUtils.js';

const SchoolDistrictIfmvHover = ({ getRows, children }) => (
  <Tooltip
    title={renderUnifiedTooltip(schoolDistrictIfmvTooltipLines(typeof getRows === 'function' ? getRows() : []))}
    placement="bottom"
    disableInteractive
  >
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
      {children}
    </Box>
  </Tooltip>
);

export const NEGOTIATING_STATUS_DISPLAYS = ['Offer Sent', 'Negotiating', 'Updated Offer', 'Uploaded Evid.'];

const courtDateFilterOperators = [
  ...getGridStringOperators(),
  {
    value: 'unassignedDate',
    label: 'Unassigned Date',
    requiresFilterValue: false,
    getApplyFilterFn: () => (_value, row) => isUnassignedCourtDateCase(row),
  },
];

export const isNegotiatingStatus = (row) => {
  const d = row.statusDisplay;
  if (typeof d !== 'string') return false;
  return NEGOTIATING_STATUS_DISPLAYS.some(s => d.startsWith(s));
};

/** Presence flags that affect which columns exist — order-independent. */
export const getColumnPresenceFlags = (rows = []) => ({
  hasNegotiatingCases: rows.some((row) => isNegotiatingStatus(row)),
  hasPortfolioOffers: rows.some((row) =>
    row.PortfolioOffer !== null &&
    row.PortfolioOffer !== 'None' &&
    row.PortfolioOffer !== '' &&
    !isNaN(Number(row.PortfolioOffer))
  ),
  hasCaseNotes: rows.some((row) => row.CaseNotes && row.CaseNotes.trim() !== ''),
});

export const createColumnDefinitions = ({
  townView,
  gridKeys = null,
  onDeleteOffer,
  focusTownOfferCell,
  onTownAcceptOffer,
  townAcceptParcelId = null,
  onTownCancelSettlement = null,
  hasNegotiatingCases = false,
  hasPortfolioOffers = false,
  hasCaseNotes = false,
  showRoundColumns = false,
  maxRounds = 0,
  hasUnassignedCourtDate = false,
  avToggleActive = false,
  isLoading = false,
  onShowDialog = null,
  setOffersChanged = null,
  reportType = 'standard',
  onFileView = null,
  getRows = null,
}) => {
  const allColumns = [
    // -------------------------
    // Base columns
    // -------------------------
    {
      field: 'Comp',
      headerName: '#',
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      width: townView ? 96 : 50,
      maxWidth: townView ? 96 : 50,
      hideable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Box sx={{ position: 'relative', alignSelf: 'stretch', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', width: '100%', height: '100%', minWidth: 0 }}>
          <span />
          <span>{row.Comp ?? ''}</span>
          <span />
          {townView && !row.loading && (
            <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', transform: 'translateY(-0.75px)' }}>
              <TownIssueMarks requests={row.DocRequests} />
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: 'Address',
      headerName: 'Address',
      align: 'left',
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => <AddressCell row={row} townView={townView} />,
    },
    {
      field: 'SDName',
      headerName: 'School District',
      type: 'string',
      align: 'left',
      minWidth: 120,
      flex: 0.7,
      editable: false,
      renderHeader: () => (
        <SchoolDistrictIfmvHover getRows={getRows}>
          School District
        </SchoolDistrictIfmvHover>
      ),
      renderCell: ({ row }) => {
        if (row?.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>—</span>;
        const name = String(row?.SDName || '').trim();
        return name ? <span>{name}</span> : <span style={{ color: '#888', fontStyle: 'italic' }}>None</span>;
      },
    },
    {
      field: 'PhotoURL',
      headerName: 'Photo',
      align: 'center',
      headerAlign: 'center',
      minWidth: 120,
      flex: 0.8,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      cellClassName: 'photo-cell-no-padding',
      disableClickEventBubbling: true,
      renderCell: ({ row }) => (
        <div style={{
          width: '100%',
          height: '100%',
          padding: 0,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <PhotoCell
            parcelId={row.parcel_id}
            photoUrl={row.PhotoURL}
            MLSPhotoFolder={row.MLSPhotoFolder}
            cellMode={townView ? 'display' : 'edit'}
            showUploadPrompt={!townView}
            style={{ width: '100%', height: '100%' }}
            arrowSize="compact"
          />
        </div>
      ),
    },
    {
      field: 'parcel_id',
      headerName: 'PID',
      type: 'string',
      align: 'left',
      flex: 0.6,
      renderCell: ({ row }) => <CopyableIdCell value={row.parcel_id} label="Parcel ID" />,
    },
    { field: 'ClientName', headerName: 'Client Name', type: 'string', align: 'left', flex: 0.6 },
    {
      field: 'SCARIndexNumber',
      headerName: 'SCAR Id',
      type: 'string',
      align: 'left',
      flex: 0.5,
      sortComparator: scarIndexSortComparator,
      renderCell: ({ row }) => <CopyableIdCell value={row.SCARIndexNumber} label="SCAR ID" />,
    },
    {
      field: 'SCARHearingDate',
      headerName: 'Court Date',
      type: 'string',
      align: 'left',
      flex: 0.8,
      minWidth: 150,
      valueGetter: (value, row) => {
        if (row.loading) return '';
        return parseCourtDateCalendarKey(row.SCARHearingDate) ?? '';
      },
      valueFormatter: (value, row) => formatCourtDateDisplayDay(row?.SCARHearingDate ?? value) || '',
      filterOperators: hasUnassignedCourtDate ? courtDateFilterOperators : undefined,
      renderCell: ({ row }) =>
        row.loading
          ? <span style={{ color: '#888', fontStyle: 'italic' }}>—</span>
          : <span>{formatCourtDateDisplayDay(row.SCARHearingDate) || '—'}</span>,
    },
    {
      field: 'PropertyAssessment',
      headerName: 'Assessment',
      type: 'string',
      align: 'left',
      flex: 0.6,
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '') return '';
        const numValue = Number(value);
        return isNaN(numValue) ? '' : numValue.toLocaleString();
      },
      // hide: !avToggleActive,
    },
    {
      field: 'IFMV',
      headerName: townView ? 'EQ. Value' : (avToggleActive ? 'Assessment' : 'IFMV'),
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: 0.8,
      valueFormatter: (value) => value != null ? `$${value.toLocaleString()}` : '',
      renderCell: ({ row }) => {
        const displayValue = townView ? row.IFMV : (avToggleActive ? row.PropertyAssessment : row.IFMV);
        if (displayValue == null || displayValue === undefined || displayValue === '') return <span style={{ color: '#888', fontStyle: 'italic' }}>None</span>;
        if (displayValue === 0 || displayValue === '0') return townView ? '$0' : (avToggleActive ? '0' : '$0');
        if (townView) return `$${Number(displayValue).toLocaleString()}`;
        return avToggleActive ? `${Math.round(displayValue).toLocaleString()}` : `$${displayValue.toLocaleString()}`;
      },
    },
    {
      field: 'Taxes',
      headerName: 'Taxes',
      type: 'number',
      align: 'left',
      flex: 0.6,
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '') return '';
        const numValue = Number(String(value).replace(/,/g, ''));
        return isNaN(numValue) ? '' : numValue.toLocaleString();
      },
    },

    // -------------------------
    // Workup columns
    // -------------------------
    {
      field: 'CompsCount',
      headerName: '# Comps',
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      flex: 0.4,
      maxWidth: 72,
      valueGetter: (value, row) => (row.loading ? null : value ?? 0),
    },
    {
      field: 'ProprietyWorkup',
      headerName: 'Workup Value',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: 1.05,
      renderCell: (params) => (
        <WorkupCell
          {...params}
          avToggleActive={avToggleActive}
          reportType={reportType}
          onFileView={onFileView}
          townView={townView}
        />
      ),
    },
    {
      field: 'RunDate',
      headerName: 'Workup run',
      type: 'string',
      align: 'left',
      flex: 0.75,
      valueGetter: (value, row) => (row.loading ? null : value),
      renderCell: ({ row }) =>
        row.loading
          ? <span style={{ color: '#888', fontStyle: 'italic' }}>—</span>
          : <span>{row.RunDate ? formatDateWithoutTimezone(row.RunDate) : '—'}</span>,
    },
    {
      field: 'ManualReview',
      headerName: 'Manual review',
      type: 'string',
      align: 'center',
      headerAlign: 'center',
      flex: 0.45,
      valueGetter: (value, row) => {
        if (row.loading) return '';
        return value === 1 || value === true ? 'True' : '';
      },
      renderCell: ({ row }) => (
        <span>{!row.loading && (row.ManualReview === 1 || row.ManualReview === true) ? 'True' : ''}</span>
      ),
    },
    {
      field: 'SalePrice',
      headerName: 'Sale Price',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: 0.7,
      valueFormatter: (value) => value != null && value !== '' && Number(value) !== 0 ? `$${Number(value).toLocaleString()}` : '',
      valueGetter: (value) => value != null && value !== '' ? Number(value) : null,
    },
    {
      field: 'SaleDate',
      headerName: 'Sale Date',
      type: 'string',
      align: 'left',
      headerAlign: 'left',
      flex: 0.9,
      valueFormatter: (value) => value != null && value !== '' ? value : '',
    },
    {
      field: 'CaseStrength',
      headerName: 'Case Strength',
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      flex: 0.5,
      renderCell: ({ row }) => {
        if (row.loading) return <CircularProgress size={16} />;
        if (row.CaseStrength === null || row.CaseStrength === undefined) return <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>;
        const val = typeof row.CaseStrength === 'number' ? row.CaseStrength : parseFloat(row.CaseStrength);
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: val > 50 ? '#16a34a' : '#dc2626', fontWeight: 'bold', fontSize: '0.875rem' }}>
            {row.CaseStrength}%
          </div>
        );
      },
    },

    // -------------------------
    // Offer columns
    // -------------------------
    {
      field: 'OfferValue',
      headerName: avToggleActive ? 'Aventine Offer (AV)' : 'Aventine Offer (MV)',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: townView ? 0.9 : 0.7,
      editable: !townView,
      valueParser: (v) => (v === '' || v == null ? null : Number(v)),
      valueGetter: (value, row) => parseRowOfferValue(row.OfferValue),
      renderEditCell: (params) => (
        <OfferEditCell params={params} avToggleActive={avToggleActive} onShowDialog={onShowDialog} setOffersChanged={setOffersChanged} party="Aventine" townView={townView} />
      ),
      renderCell: ({ row }) => {
        if (row.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
        return <OfferCell row={row} avToggleActive={avToggleActive} townView={townView} party="Aventine" />;
      },
    },
    {
      field: 'RequestedAV',
      headerName: avToggleActive ? 'Requested MV' : 'Requested AV',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: 0.9,
      renderCell: ({ row }) => {
        if (row.loading) return <ValueCell value={null} loading type="av" />;
        if (townView && row._townAventineDisplayNotAvailable) {
          return <span style={{ color: '#888', fontStyle: 'italic' }}>Not Available</span>;
        }
        if (!avToggleActive) return <ValueCell value={row.RequestedAV} loading={row.loading} type="av" pctReduction={townView ? null : row.pctReduction} />;
        const marketValue = row.RequestedAV ? convertAVToMV(row.RequestedAV, row.EQ, row.RAR) : row.RequestedAV;
        return <ValueCell value={marketValue} loading={row.loading} type="market" pctReduction={townView ? null : row.pctReduction} />;
      },
    },
    {
      field: 'Comp1',
      headerName: 'Comp 1',
      type: 'string',
      align: 'left',
      flex: 0.8,
      valueGetter: (value, row) => (row.loading ? '' : value),
    },
    {
      field: 'Comp2',
      headerName: 'Comp 2',
      type: 'string',
      align: 'left',
      flex: 0.8,
      valueGetter: (value, row) => (row.loading ? '' : value),
    },
    {
      field: 'Comp3',
      headerName: 'Comp 3',
      type: 'string',
      align: 'left',
      flex: 0.8,
      valueGetter: (value, row) => (row.loading ? '' : value),
    },
    {
      field: 'Comp4',
      headerName: 'Comp 4',
      type: 'string',
      align: 'left',
      flex: 0.8,
      valueGetter: (value, row) => (row.loading ? '' : value),
    },
    {
      field: 'Comp5',
      headerName: 'Comp 5',
      type: 'string',
      align: 'left',
      flex: 0.8,
      valueGetter: (value, row) => (row.loading ? '' : value),
    },
    {
      field: 'TownOffer',
      headerName: townView
        ? (avToggleActive ? 'Town Counter (AV)' : 'Town Counter (MV)')
        : (avToggleActive ? 'Town Offer (AV)' : 'Town Offer (MV)'),
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: townView ? 0.8 : 0.7,
      editable: true,
      valueParser: (v) => (v === '' || v == null ? null : Number(v)),
      valueGetter: (value, row) => parseRowOfferValue(row.TownOffer),
      renderEditCell: (params) => (
        <OfferEditCell params={params} avToggleActive={avToggleActive} onShowDialog={onShowDialog} setOffersChanged={setOffersChanged} party="Town" townView={townView} />
      ),
      renderCell: ({ row }) => {
        if (row.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
        return <OfferCell row={row} avToggleActive={avToggleActive} townView={townView} party="Town" />;
      },
    },
    {
      field: 'TownReqAV',
      headerName: townView
        ? (avToggleActive ? 'Counter MV' : 'Counter AV')
        : (avToggleActive ? 'Town Req MV' : 'Town Req AV'),
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: 0.7,
      renderCell: ({ row }) => {
        if (!avToggleActive) return <ValueCell value={row.TownReqAV} loading={row.loading} type="av" pctReduction={townView ? null : row.townPctReduction} />;
        const marketValue = row.TownReqAV ? convertAVToMV(row.TownReqAV, row.EQ, row.RAR) : row.TownReqAV;
        return <ValueCell value={marketValue} loading={row.loading} type="market" pctReduction={townView ? null : row.townPctReduction} />;
      },
    },
    {
      field: 'OfferDiff',
      headerName: 'Offer Diff',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      flex: 0.6,
      hide: true,
      renderCell: ({ row }) => {
        if (row.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
        if (row.OfferDiff === null || row.OfferDiff === undefined) return <span style={{ color: '#888', fontStyle: 'italic' }}>-</span>;
        let displayValue = row.OfferDiff;
        if (!avToggleActive) displayValue = convertAVToMV(row.OfferDiff, row.EQ, row.RAR);
        return (
          <span style={{ color: displayValue > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 'bold' }}>
            {avToggleActive ? `${Math.round(displayValue).toLocaleString()} AV` : `${(Math.round(displayValue / 10) * 10).toLocaleString()}`}
          </span>
        );
      },
    },

    // -------------------------
    // Portfolio columns (conditional)
    // -------------------------
    ...(hasPortfolioOffers ? [
      {
        field: 'PortfolioOffer',
        headerName: 'Portfolio Offer',
        type: 'number',
        align: 'left',
        flex: 0.6,
        renderCell: ({ row }) => {
          if (row.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
          const hasValue = row.PortfolioOffer !== null && row.PortfolioOffer !== 'None' && row.PortfolioOffer !== '' && !isNaN(Number(row.PortfolioOffer));
          if (!hasValue) return '';
          const displayValue = !avToggleActive ? convertAVToMV(Number(row.PortfolioOffer), row.EQ, row.RAR) : Number(row.PortfolioOffer);
          return `$${(Math.round(displayValue / 10) * 10).toLocaleString()}`;
        },
      },
      {
        field: 'PortfolioRequestedAV',
        headerName: 'Portfolio Requested AV',
        type: 'number',
        align: 'left',
        flex: 0.6,
        editable: townView,
        hide: avToggleActive,
        renderCell: ({ row }) => (
          <span>
            {row.loading
              ? <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>
              : (row.PortfolioRequestedAV !== null && row.PortfolioRequestedAV !== 'None' && row.PortfolioRequestedAV !== '' && !isNaN(Number(row.PortfolioRequestedAV))
                ? (Math.round(Number(row.PortfolioRequestedAV) / 10) * 10).toLocaleString()
                : '')}
          </span>
        ),
      },
    ] : []),

    // -------------------------
    // Town Response (town portal only; internal negotiating grid has no town actions here)
    // -------------------------
    ...(townView ? [{
      field: 'TownResponse',
      headerName: 'Town Response',
      align: 'center',
      headerAlign: 'center',
      flex: 1.2,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
        return (
          <TownResponseCell
            key={row.parcel_id}
            row={row}
            townAcceptParcelId={townAcceptParcelId}
            focusTownOfferCell={focusTownOfferCell}
            onTownAcceptOffer={onTownAcceptOffer}
            onTownCancelSettlement={onTownCancelSettlement}
          />
        );
      },
    }] : []),

    // -------------------------
    // Town / negotiating round notes (single column: internal when there are negotiating cases; always in town view)
    // -------------------------
    ...((townView || hasNegotiatingCases) ? [{
      field: 'TownNotes',
      headerName: 'Notes',
      type: 'string',
      align: 'left',
      flex: 1,
      editable: false,
      sortable: false,
      disableColumnFilter: true,
      disableColumnMenu: true,
      disableColumnSort: true,
      renderCell: ({ row }) => (
        <EditableNotesComponent
          negotiations={row.Negotiations}
          loading={row.loading}
          parcelId={row.parcel_id}
          townView={townView}
          notesDisabledReason={row.notesDisabledReason}
          isActive={row.isActive !== false}
          onUpdateSuccess={() => logger.log('Notes updated for parcel:', row.parcel_id)}
        />
      ),
    }] : []),

    // -------------------------
    // Internal notes (conditional)
    // -------------------------
    ...(hasCaseNotes ? [{
      field: 'CaseNotes',
      headerName: 'Internal Notes',
      type: 'string',
      align: 'left',
      flex: 0.67,
      renderCell: ({ row }) => {
        if (row.loading) return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
        if (!row.CaseNotes || row.CaseNotes.trim() === '') return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
        return (
          <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{row.CaseNotes}</span>} placement="top" disableInteractive>
            <Box component="span" sx={{ color: 'black', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{row.CaseNotes}</Box>
          </Tooltip>
        );
      },
    }] : []),

    // -------------------------
    // Status (non-town view only)
    // -------------------------
    ...(!townView ? [{
      field: 'status',
      headerName: 'Status',
      type: 'string',
      align: 'left',
      flex: 0.95,
      sortable: true,
      valueGetter: (value, row) => row.statusDisplay || '',
      renderCell: ({ row }) => {
        const statusStaysBlack = row.statusDisplay === 'Awaiting Decision' || row.statusDisplay === 'ADMIN HOLD';
        const statusLabelStyle = {
          ...row.statusStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: '1',
          height: '20px',
          width: '100%',
          flex: '1',
          ...(statusStaysBlack ? { color: '#000000' } : {}),
        };
        const statusContent = (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '100%', minHeight: '32px', width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '32px', flex: '1', minWidth: 0 }}>
              <span
                className={statusStaysBlack ? 'summary-status-awaiting-decision' : undefined}
                style={statusLabelStyle}
              >
                {row.statusDisplay}
              </span>
              {row.statusSubtext && (
                <span style={{ fontSize: '0.65rem', color: row.statusStyle?.color || '#666', fontWeight: 'normal', lineHeight: '1', height: '12px', marginTop: '2px' }}>{row.statusSubtext}</span>
              )}
            </div>
            {row.statusIcon && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '20px', width: '20px', flexShrink: 0 }}>
                {row.statusIcon}
              </div>
            )}
          </div>
        );
        return row.statusTooltip ? (
          <Tooltip
            title={<div>{row.statusTooltip.split('\n').map((line, index) => <div key={index}>{line}</div>)}</div>}
            placement="top"
            disableInteractive
            slotProps={{ transition: { timeout: 0 }, popper: { popperOptions: { modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } }, { name: 'flip', enabled: false }] } } }}
          >
            {statusContent}
          </Tooltip>
        ) : statusContent;
      },
    }] : []),
  ];

  // Insert Rep ID before Status (or at end if no status column)
  const statusIndex = allColumns.findIndex(col => col.field === 'status');
  const repIdInsertIndex = statusIndex !== -1 ? statusIndex : allColumns.length;
  allColumns.splice(repIdInsertIndex, 0, {
    field: 'RepID',
    headerName: 'Rep ID',
    type: 'string',
    align: 'center',
    flex: 0.6,
  });

  // Insert round columns before OfferValue if enabled
  if (showRoundColumns && maxRounds > 0) {
    const offerValueIndex = allColumns.findIndex(col => col.field === 'OfferValue');
    const roundInsertIndex = offerValueIndex !== -1 ? offerValueIndex : allColumns.length;

    for (let roundNum = 1; roundNum <= maxRounds; roundNum++) {
      allColumns.splice(roundInsertIndex + roundNum - 1, 0, {
        field: `Round${roundNum}`,
        headerName: `Round ${roundNum}`,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        editable: false,
        renderCell: ({ row }) => {
          const negotiations = row.Negotiations || [];
          const roundData = negotiations.find(round => round.round === roundNum);

          if (!roundData?.offers?.length) {
            return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '32px', fontSize: '0.75rem', color: '#999' }}>-</div>;
          }

          const aventineOffer = roundData.offers.find(o => o.OfferedBy === 'Aventine');
          const townOffer = roundData.offers.find(o => o.OfferedBy === 'Town');

          const formatOfferValue = (offer) => {
            if (!offer?.Offer) return avToggleActive ? '0 AV' : '$0';
            let val = Number(offer.Offer);
            if (!avToggleActive) val = convertAVToMV(val, row.EQ, row.RAR);
            return avToggleActive ? `${Math.round(val).toLocaleString()} AV` : `$${(Math.round(val / 10) * 10).toLocaleString()}`;
          };

          const renderOfferChip = (offer, party) => {
            const color = party === 'Aventine' ? '#1976d2' : '#e65100';
            if (!offer) return <span style={{ color: '#999' }}>-</span>;
            return (
              <Tooltip
                placement="top"
                title={
                  <div>
                    <div><strong>{party} Offer</strong></div>
                    <div>Amount: {formatOfferValue(offer)}</div>
                    <div>{offer.LiveOffer === 1 ? 'Sent' : 'Updated'} by: {getUserNameById(offer.Negotiator)}</div>
                    <div>{offer.LiveOffer === 1 ? 'Sent' : 'Updated'}: {offer.OfferDate ? formatCourtDateDisplayDay(offer.OfferDate) : 'N/A'}</div>
                    {offer.Comments && <div>Comments: {offer.Comments}</div>}
                    {offer.OfferID && !townView && (
                      <div style={{ marginTop: '8px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteOffer?.({ offerId: offer.OfferID, parcelId: row.parcel_id, offeredBy: party, roundNumber: roundNum }); }}
                          style={{ background: '#f44336', border: 'none', color: 'white', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}
                        >
                          Delete Offer
                        </button>
                      </div>
                    )}
                  </div>
                }
              >
                <span style={{ color, fontWeight: 'bold', cursor: 'help' }}>{formatOfferValue(offer)}</span>
              </Tooltip>
            );
          };

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', height: '100%', minHeight: '32px', fontSize: '0.75rem' }}>
              {renderOfferChip(aventineOffer, 'Aventine')}
              <span style={{ color: '#666' }}>/</span>
              {renderOfferChip(townOffer, 'Town')}
            </div>
          );
        },
      });
    }
  }

  // Town view: PID before Address; Town Response → Town Counter (TownOffer) → Counter AV / MV (TownReqAV)
  if (townView) {
    const addressIdx = allColumns.findIndex((c) => c.field === 'Address');
    const pidIdx = allColumns.findIndex((c) => c.field === 'parcel_id');
    if (addressIdx !== -1 && pidIdx !== -1 && pidIdx > addressIdx) {
      const [pidCol] = allColumns.splice(pidIdx, 1);
      allColumns.splice(addressIdx, 0, pidCol);
    }

    const anchorIdx = allColumns.findIndex((c) => c.field === 'TownResponse');
    const moveFields = ['TownOffer', 'TownReqAV'];
    const removals = moveFields.map((field) => {
      const idx = allColumns.findIndex((c) => c.field === field);
      return idx !== -1 ? { idx } : null;
    }).filter(Boolean);
    removals.sort((a, b) => b.idx - a.idx);

    if (anchorIdx !== -1 && removals.length) {
      const pulled = {};
      for (const { idx } of removals) {
        const [col] = allColumns.splice(idx, 1);
        pulled[col.field] = col;
      }
      let insertAt = allColumns.findIndex((c) => c.field === 'TownResponse');
      if (insertAt !== -1) {
        moveFields.forEach((field) => {
          const col = pulled[field];
          if (col) {
            allColumns.splice(insertAt + 1, 0, col);
            insertAt++;
          }
        });
      }
    }
  }

  const defined = new Set(allColumns.map((col) => col.field));
  const generated = CASE_REVIEW_COLUMNS
    .filter((spec) => spec.summary && spec.headerName && !defined.has(spec.field))
    .map((spec) => ({
      field: spec.field,
      headerName: spec.headerName,
      type: 'string',
      align: 'left',
      headerAlign: 'left',
      flex: spec.flex ?? 0.8,
    }));
  if (generated.length) {
    const saleDateIdx = allColumns.findIndex((col) => col.field === 'SaleDate');
    allColumns.splice(saleDateIdx === -1 ? allColumns.length : saleDateIdx + 1, 0, ...generated);
  }

  if (!Array.isArray(gridKeys) || gridKeys.length === 0) return allColumns;

  const allowed = new Set(gridKeys);
  const alwaysKeep = new Set(['Comp']); // synthetic row-number column
  const keep = (col) =>
    alwaysKeep.has(col.field) ||
    allowed.has(col.field) ||
    (typeof col.field === 'string' && col.field.startsWith('Round'));

  return allColumns.filter(keep);
};